import { useState, useEffect } from 'react';
import {
    Typography,
    TextField,
    Button,
    Paper,
    Stack,
    Box
} from '@mui/material';
import CustomSnackbar from '../../CustomSnackbar';
import EmailInput, { validateEmails } from './EmailInput';
import PreviewNewsletter from './PreviewNewsletter';
import ConfirmationDialog from './ConfirmationDialog';
import NewsletterEditor from './NewsletterEditor';
import { sendNewsletter } from '../../services/user_Service';
import { wrapNewsletterWithTemplate } from '../../utills/wrapNewsletterWithTemplate';
import  config  from "../../../config";
const apiurl = config.NEWSLETTER_URL;

const MAX_SUBJECT_LENGTH = 100;
const MAX_BODY_LENGTH = 5000;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

const Create = ({ draft, clearDraft }) => {
    const [recipients, setRecipients] = useState([]);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [openPreview, setOpenPreview] = useState(false);

    useEffect(() => {
        if (draft) {
            setSubject(draft.subject || '');
            setBody(draft.content || '');
            setRecipients(draft.recipients || []);
            clearDraft();
        }
    }, [draft, clearDraft]);

    const handleBodyChange = (newValue) => {
        console.log('Body changed:', newValue);
        setBody(newValue);
    };

    const handleSend = async () => {
        const hasText = body.replace(/<[^>]*>?/gm, '').trim().length > 0;
        const imgTags = body.match(/<img[^>]+src=["'](.*?)["'][^>]*>/gi) || [];
        const hasImage = imgTags.length > 0;

        const errors = [];

        const emailErrors = validateEmails(recipients);
        errors.push(...emailErrors);

        if (!subject.trim()) {
            errors.push('Subject is required.');
        }
        if (subject.length > MAX_SUBJECT_LENGTH) {
            errors.push(`Subject must be under ${MAX_SUBJECT_LENGTH} characters.`);
        }
        if (!hasText && !hasImage) {
            errors.push('Body cannot be empty. Please add text or images.');
        }
        if (hasText.length > MAX_BODY_LENGTH) {
            errors.push(`Body must be under ${MAX_BODY_LENGTH} characters.`);
        }

        if (errors.length > 0) {
            setStatus(errors.join(' '));
            return;
        }

        setLoading(true);
        setStatus('');

        const imageFiles = [];
        let updatedBody = body;

        if (hasImage) {
            console.log(`Processing ${imgTags.length} images...`);

            for (let i = 0; i < imgTags.length; i++) {
                const imgTag = imgTags[i];
                const srcMatch = imgTag.match(/src=["'](.*?)["']/i);

                if (srcMatch && srcMatch[1]) {
                    const src = srcMatch[1];
                    console.log(`Processing image ${i + 1} with src:`, src);

                    // Skip processing if the src is a URL (not a base64 data URL)
                    if (!src.startsWith('data:')) {
                        console.log(`Skipping image ${i + 1} as it is a URL: ${src}`);
                        continue;
                    }

                    try {
                        // Handle base64 data URLs
                        const response = await fetch(src);
                        const blob = await response.blob();

                        if (!ALLOWED_MIME_TYPES.includes(blob.type)) {
                            throw new Error(`Invalid image type. Allowed types: ${ALLOWED_MIME_TYPES.join(', ')}.`);
                        }
                        if (blob.size === 0) {
                            throw new Error('Image file is empty.');
                        }
                        if (blob.size > MAX_FILE_SIZE) {
                            throw new Error('Image size exceeds 5MB limit.');
                        }

                        const fileExtension = blob.type.split('/')[1] || 'jpg';
                        const timestamp = Date.now();
                        const fileName = `newsletter_image_${timestamp}_${i + 1}.${fileExtension}`;
                        const imageFile = new File([blob], fileName, { type: blob.type });

                        imageFiles.push(imageFile);

                        const newImageUrl = `${apiurl}/non-auth-user/retrieve-media/images/newsletter/${fileName}`;
                        updatedBody = updatedBody.replace(imgTag, `<img src="${newImageUrl}" alt="Newsletter Image" style="max-width: 100%; height: auto;" />`);

                        console.log(`Processed image ${i + 1}: ${fileName}`);

                    } catch (err) {
                        console.error(`Error converting image ${i + 1} to File:`, err);
                        setStatus(`Failed to process image ${i + 1}: ` + err.message);
                        setLoading(false);
                        return;
                    }
                } else {
                    setStatus(`No valid image source found in image ${i + 1}.`);
                    setLoading(false);
                    return;
                }
            }
        }

        // updatedBody = wrapNewsletterWithTemplate(updatedBody);
        // console.log('Final body after wrapping with template:', updatedBody);

        const formData = new FormData();
        // recipients.forEach(email => formData.append('recipients[]', email));
        formData.append('recipients', JSON.stringify(recipients));
        formData.append('subject', subject);
        formData.append('content', updatedBody);

        if (imageFiles.length > 0) {
            imageFiles.forEach((imageFile, index) => {
                formData.append(`banner_images[]`, imageFile);
                console.log(`Appending image file ${index + 1}:`, {
                    name: imageFile.name,
                    size: imageFile.size,
                    type: imageFile.type
                });
            });
        }

        console.log('FormData contents:');
        for (const [key, value] of formData.entries()) {
            if (value instanceof File) {
                console.log(`  ${key}:`, {
                    name: value.name,
                    size: value.size,
                    type: value.type,
                    isFile: true
                });
            } else {
                console.log(`  ${key}:`, value);
            }
        }

        try {
            const response = await sendNewsletter(formData);
            console.log('Newsletter sent:', response);
            setStatus('Newsletter sent successfully!');
            setRecipients([]);
            setSubject('');
            setBody('');
            clearDraft();
        } catch (err) {
            console.error('Error sending newsletter:', err);
            const errorMessage = err.response?.data?.message
                ? typeof err.response.data.message === 'object'
                    ? Object.values(err.response.data.message).flat().join(' ')
                    : err.response.data.message
                : 'Failed to send newsletter.';
            setStatus(errorMessage);
        } finally {
            setLoading(false);
            setConfirmOpen(false);
        }
    };

    return (
        <>
            <Paper elevation={0} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        Create Newsletter
                    </Typography>
                    <Button 
                    sx={{ textTransform: 'none' }}
                    disabled={loading || !subject || !body}
                    onClick={() => {
                        setRecipients([]);
                        setSubject('');
                        setBody('');
                    }}>
                        Clear
                    </Button>
                </Stack>

                <Box className="newsletter-form-section">
                    <EmailInput value={recipients} onChange={setRecipients} />
                </Box>

                <Box className="newsletter-form-section">
                    <TextField
                        label="Subject"
                        placeholder="Enter subject"
                        variant="outlined"
                        fullWidth
                        size='small'
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        helperText="Max 100 characters"
                    />
                </Box>

                <Box className="newsletter-form-section">
                    <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                        Message Body
                    </Typography>

                    <NewsletterEditor value={body} onChange={handleBodyChange} />
                </Box>

                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setOpenPreview(true)}
                        disabled={loading || !subject || !body}
                        sx={{ textTransform: 'none' }}
                    >
                        Preview
                    </Button>

                    <Button
                        disableElevation
                        variant="contained"
                        color="primary"
                        onClick={() => setConfirmOpen(true)}
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                    >
                        Send Newsletter
                    </Button>
                </Stack>

                {status && (
                    <CustomSnackbar
                        open={!!status}
                        message={status}
                        severity={status.includes('successfully') ? 'success' : 'error'}
                        onClose={() => setStatus('')}
                    />
                )}
            </Paper>

            <ConfirmationDialog
                open={confirmOpen}
                loading={loading}
                onClose={() => setConfirmOpen(false)}
                onSend={handleSend}
            />

            <PreviewNewsletter
                open={openPreview}
                onClose={() => setOpenPreview(false)}
                subject={subject}
                body={body} // Wrap body content with template
            />
        </>
    );
};

export default Create;